import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button — 5 variant. spec §10 / styles.css `.btn-*` 매핑.
 * `primary`: Toss Blue 채움
 * `ghost`: 텍스트만, hover 시 surface-alt
 * `outline`: 1px border + 흰 배경
 * `soft`: primary-soft 배경 + primary 텍스트
 * `accent`: text 색 배경 (검정 톤) — CTA 보조
 */
const buttonStyles = cva(
  [
    'inline-flex items-center justify-center gap-1.5 select-none',
    // V3 애플 재질(D-05): 스프링 easing + 누를 때 살짝 눌리는 탄성.
    'font-bold transition-all ease-spring active:scale-[0.97]',
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-white hover:bg-primary-hover hover:shadow-lift',
        ghost:
          'text-text-2 hover:bg-surface-alt hover:text-text font-semibold',
        outline:
          'border border-border-strong bg-surface text-text hover:bg-surface-alt',
        soft: 'bg-primary-soft text-primary hover:bg-primary-soft-hover',
        accent: 'bg-text text-white hover:bg-black',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-md',
        md: 'h-10 px-4 text-sm rounded-md',
        lg: 'h-12 px-5 text-[15px] rounded-lg',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      block: false,
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonStyles({ variant, size, block }), className)}
      {...rest}
    />
  ),
)
Button.displayName = 'Button'
